import { Currency, Locale } from './CurrencyInput';

export const sanitizePartialCurrencyValue = (
  value: string,
  currency: Currency,
) => {
  // remove any character not 0-9 or . or ,
  let sanitizedValue = value.replaceAll(/[^(0-9).,]/g, '');

  // removes thousands separators
  // changes minor units separator to . if needed
  // removes .xx if minor units is false
  switch (currency.locale) {
    case 'de-DE':
      sanitizedValue = sanitizedValue.replaceAll('.', '');
      if (currency.minorUnits) {
        sanitizedValue = sanitizedValue.replaceAll(',', '.');
      } else {
        sanitizedValue = sanitizedValue.replaceAll(/,\d*$/g, '');
      }
      break;
    case 'en-GB':
    default:
      sanitizedValue = sanitizedValue.replaceAll(',', '');
      if (!currency.minorUnits) {
        sanitizedValue = sanitizedValue.replaceAll(/\.\d*$/g, '');
      }
  }

  return sanitizedValue;
};

export const formatAsPartialCurrency = (
  sanitizedValue: string,
  currency: Currency,
) => {
  const decimalSeparator = getDecimalSeperator(currency.locale);

  let value = sanitizedValue.trim();
  if (value === '') {
    return '';
  }

  // trying to type third decimal place
  if (value.match(/(\.\d{3})$/)) {
    value = value.split('').slice(0, -1).join('');
  }

  // typing second decimal place
  if (value.match(/(\.\d{2})$/)) {
    return formatAsCurrency(value, currency);
  }

  // starting to type minor units
  const startingMinorUnits = !!value.match(/\.$/);
  const starting0 = !!value.match(/\.0$/);
  value = value.replace(/\.$/, '');

  // format to partial currency string
  let currencyString = Number(value).toLocaleString(currency.locale, {
    maximumFractionDigits: 2,
  });

  if (startingMinorUnits) {
    currencyString = `${currencyString}${decimalSeparator}`;
  }
  if (starting0) {
    currencyString = `${currencyString}${decimalSeparator}0`;
  }

  if (currency?.currencySymbol) {
    switch (currency.locale) {
      case 'de-DE':
        return `${currencyString} €`;
      case 'en-GB':
        return `£ ${currencyString}`;
    }
  }

  return currencyString;
};

export const formatAsCurrency = (value: string, currency: Currency) => {
  value = value.trim();
  if (value === '') {
    return '';
  }

  const formatter = new Intl.NumberFormat(currency.locale, {
    style: currency.currencySymbol ? 'currency' : undefined,
    currency: currency.locale === 'de-DE' ? 'EUR' : 'GBP',
    minimumFractionDigits: currency.minorUnits ? 2 : 0,
    maximumFractionDigits: currency.minorUnits ? 2 : 0,
  });

  let formattedValue = formatter.format(Number(value));

  switch (currency.locale) {
    case 'de-DE':
      formattedValue = formattedValue.replace(/\s(\D+)$/, ' $1'); //enforce correct space character before €
      formattedValue = currency.currencySymbol
        ? formattedValue.replace(/,00\s€$/, ' €')
        : formattedValue.replace(/,00$/, '');
      break;
    case 'en-GB':
      formattedValue = formattedValue
        .replace(/^(\D+)/, '$1 ') // Add space after £ symbol
        .replace(/\.00$/, '');
      break;
  }

  return formattedValue;
};

const getDecimalSeperator = (locale: Locale) => {
  switch (locale) {
    case 'de-DE':
      return ',';
    case 'en-GB':
      return '.';
  }
};

export const updateCursorLocation = (
  ev: React.ChangeEvent<HTMLInputElement>,
  currency: Currency,
) => {
  if (currency?.locale === 'de-DE' && currency?.currencySymbol) {
    const valueLength = ev.target.value.length - 2;
    ev.currentTarget.setSelectionRange(valueLength, valueLength);
  }
};
